/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 13:44:49
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  CHANGE_ACTIVE_MENU_ITEM,
  CHANGE_SELECTED_PAGE,
  UPDATE_MENU_SETTING,
  REMOVE_ELEMENT,
  TOGGLE_HIDE_MENU_ITEM,
  UPDATE_WHOLE_MENU_SETTING,
  TOGGLE_EXPAND_FULLSCREEN,
  GET_THEME_SETTING_BY_ID_REQUEST,
  GET_THEME_SETTING_BY_ID_SUCCESS,
  GET_THEME_SETTING_BY_ID_FAILURE,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE,
  UPDATE_THEME_SETTING,
  EDIT_THEME_SETTING_BY_ID_REQUEST,
  EDIT_THEME_SETTING_BY_ID_SUCCESS,
  EDIT_THEME_SETTING_BY_ID_FAILURE,
  CHANGE_DEVICE
} from 'stores/flow-type/shopify-integration/shopify-integration-settings.type';

/**
 * Call to update field in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateField = (fieldName: string, data: any) => {
  return { type: UPDATE_FIELD, payload: { fieldName, data } };
};

/**
 * Call to update multi fields in reducer
 * @returns An object with a type and a payload.
 */
export const handleUpdateMultiFields = (data: any) => {
  return { type: UPDATE_MULTI_FIELDS, payload: { data } };
};

/**
 * Call to change active menu item
 * @returns An object with a type and a payload.
 */
export const handleChangeActiveMenuItem = (id: string, updateType: string) => {
  return { type: CHANGE_ACTIVE_MENU_ITEM, payload: { id, updateType } };
};

/**
 * Call to change selected preview page
 * @returns An object with a type and a payload.
 */
export const handleChangeSelectedPage = (page: { label: string, value: string }) => {
  return { type: CHANGE_SELECTED_PAGE, payload: page };
};

/**
 * Call to update menu setting
 * @returns An object with a type and a payload.
 */
export const handleUpdateMenuSetting = (id: string, children: any[], updateType: string) => {
  return { type: UPDATE_MENU_SETTING, payload: { id, children, updateType } };
};

/**
 * Call to update whole menu setting
 * @returns An object with a type and a payload.
 */
export const handleUpdateWholeMenuSetting = (children: any[], updateType: string) => {
  return { type: UPDATE_WHOLE_MENU_SETTING, payload: { children, updateType } };
};

/**
 * Call to remove element of menu items
 * @returns An object with a type and a payload.
 */
export const handleRemoveElement = (activeMenuItem = null, updateType = null) => {
  return { type: REMOVE_ELEMENT, payload: { activeMenuItem, updateType } };
};

/**
 * Call to toggle hide menu item
 * @returns An object with a type and a payload.
 */
export const handleToggleHideMenuItem = (id: string, updateType: string) => {
  return { type: TOGGLE_HIDE_MENU_ITEM, payload: { id, updateType } };
};

/**
 * Call to toggle update theme setting
 * @returns An object with a type and a payload.
 */
export const updateThemeSetting = (setting: any, i18n: I18n) => {
  return { type: UPDATE_THEME_SETTING, payload: { setting, i18n } };
};

/**
 * call to get theme setting by id
 * @return {object} The RSAA object for API middleware
 */
export const getTemplateSettingById = (templateId: string, i18n: I18n, type = '') => {
  let queries = {};

  if (!_.isEmpty(type)) {
    queries.type = type;
  }
  return apiService.sendRequest(
    'shopify-integration.template-instance-settings.get-template-instance-settings-by-id',
    { params: { templateId }, queries },
    [
      GET_THEME_SETTING_BY_ID_REQUEST,
      {
        type: GET_THEME_SETTING_BY_ID_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { response: data, i18n };
        }
      },
      {
        type: GET_THEME_SETTING_BY_ID_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetting.Actions.getThemeSettingById_Failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to get theme setting default value by id
 * @return {object} The RSAA object for API middleware
 */
export const getThemeSettingDefaultValueByThemeNameLib = (themeNameLib: string, i18n: I18n) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance-settings.get-template-instance-settings-default-value-by-theme-name-lib',
    { params: { themeNameLib } },
    [
      GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST,
      {
        type: GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate(
              'AdminHTML.ThemeSetting.Actions.getThemeSettingDefaultValueById_Failure'
            ),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to edit theme settings api
 * @return {object} The RSAA object for API middleware
 */
export const editThemeSettings = (
  templateId: string,
  data: any,
  // files: Array<Object>,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'shopify-integration.template-instance-settings.edit-template-instance-settings-by-id',
    { params: { templateId }, body: data },
    [
      EDIT_THEME_SETTING_BY_ID_REQUEST,
      {
        type: EDIT_THEME_SETTING_BY_ID_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            i18n.translate('AdminHTML.ThemeSetting.Actions.editThemeSettingById_Success'),
            i18n
          );
          return { data };
        }
      },
      {
        type: EDIT_THEME_SETTING_BY_ID_FAILURE,
        payload: async (action, state, res) => {
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.ThemeSetting.Actions.editThemeSettingById_Failure'),
            i18n
          );
          return res;
        }
      }
    ]
  );
};

/**
 * Call to toggle expand right panel
 * @returns An object with a type and a payload.
 */
export const handleToggleFullscreen = (value: boolean) => {
  return { type: TOGGLE_EXPAND_FULLSCREEN, payload: value };
};

export const handleChangeDevice = (value: number) => {
  return { type: CHANGE_DEVICE, payload: value };
};
